/**
 * Prestataire invoices (FR-SUI-14): an invoice entered or imported with its number, date, total
 * and lines, each line matched to a cost line of an expédition; the difference between the
 * invoiced and the computed amounts is shown per line and flagged beyond a tolerance; finance
 * approves or disputes the invoice.
 */
import { z } from 'zod';
import { EXPEDITION_COST_KINDS } from './costs.js';
export const INVOICE_STATUSES = ['DRAFT', 'APPROVED', 'DISPUTED'];
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
const uuid = z.string().uuid();
const amount = z.number().min(-1_000_000_000).max(1_000_000_000);
/** A line of the invoice: matched to a cost line of an expédition, or free (no match). */
export const invoiceLineSchema = z.object({
    costLineId: uuid.optional(),
    kind: z.enum(EXPEDITION_COST_KINDS),
    label: z.string().trim().max(200).optional(),
    quantity: z.number().min(0).max(1_000_000).optional(),
    unit: z.string().max(20).optional(),
    unitPrice: amount.optional(),
    amount,
});
/** The tolerance beyond which a difference is flagged: the larger of a share and an amount. */
export const toleranceSchema = z.object({
    pct: z.number().min(0).max(100).default(2),
    amount: z.number().min(0).max(1_000_000).default(50),
});
export const DEFAULT_TOLERANCE = { pct: 2, amount: 50 };
export const invoiceCreateSchema = z
    .object({
    companyId: uuid,
    prestataireId: uuid,
    number: z.string().trim().min(1).max(60),
    invoiceDate: isoDate,
    dueDate: isoDate.optional(),
    total: amount,
    currency: z.string().length(3).default('MAD'),
    tolerance: toleranceSchema.default(DEFAULT_TOLERANCE),
    notes: z.string().trim().max(2000).optional(),
    lines: z.array(invoiceLineSchema).max(500).default([]),
})
    .refine((v) => !v.dueDate || v.dueDate >= v.invoiceDate, {
    path: ['dueDate'],
    message: 'dueDate must be on or after invoiceDate',
});
/** What a draft may change; the company and the prestataire stay. */
export const invoiceUpdateSchema = z.object({
    number: z.string().trim().min(1).max(60).optional(),
    invoiceDate: isoDate.optional(),
    dueDate: isoDate.nullable().optional(),
    total: amount.optional(),
    tolerance: toleranceSchema.optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    lines: z.array(invoiceLineSchema).max(500).optional(),
});
/** Finance approves or disputes; back to draft to correct it. A dispute carries its reason. */
export const invoiceDecisionSchema = z
    .object({
    status: z.enum(INVOICE_STATUSES),
    reason: z.string().trim().max(1000).optional(),
})
    .refine((v) => v.status !== 'DISPUTED' || Boolean(v.reason), {
    path: ['reason'],
    message: 'A dispute needs its reason',
});
export const invoiceListQuerySchema = z.object({
    prestataireId: uuid.optional(),
    companyId: uuid.optional(),
    status: z.enum(INVOICE_STATUSES).optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
});
/** The cost lines an invoice may match: a prestataire's, on the expéditions of a period. */
export const invoiceCandidatesQuerySchema = z.object({
    prestataireId: uuid,
    companyId: uuid,
    from: isoDate,
    to: isoDate,
});
const round2 = (n) => Math.round(n * 100) / 100;
/** Whether an invoiced amount stays within the tolerance of the computed one. */
export function withinTolerance(invoiced, computed, tolerance) {
    const allowed = Math.max(tolerance.amount, (Math.abs(computed) * tolerance.pct) / 100);
    return Math.abs(invoiced - computed) <= allowed + 1e-9;
}
/**
 * Compares each line with the cost line it matches and the invoice total with the computed
 * total, flagging what exceeds the tolerance (FR-SUI-14).
 */
export function reconcileInvoice(total, lines, tolerance = DEFAULT_TOLERANCE) {
    const reconciled = lines.map((line) => {
        const computed = line.computedAmount;
        const difference = computed === null ? null : round2(line.amount - computed);
        return {
            line,
            computedAmount: computed,
            difference,
            flagged: computed !== null && !withinTolerance(line.amount, computed, tolerance),
        };
    });
    const linesTotal = round2(lines.reduce((s, l) => s + l.amount, 0));
    const matched = lines.filter((l) => l.computedAmount !== null);
    const computedTotal = round2(matched.reduce((s, l) => s + (l.computedAmount ?? 0), 0));
    return {
        lines: reconciled,
        linesTotal,
        computedTotal,
        difference: round2(total - computedTotal),
        linesGap: round2(total - linesTotal),
        unmatched: lines.length - matched.length,
        flaggedLines: reconciled.filter((l) => l.flagged).length,
        flagged: matched.length > 0 && !withinTolerance(total, computedTotal, tolerance),
    };
}
export const INVOICE_STATUS_LABELS = {
    DRAFT: 'À vérifier',
    APPROVED: 'Approuvée',
    DISPUTED: 'Contestée',
};
/**
 * Exports to the customer's ERP (FR-SUI-15): the cost lines of a period and the approved
 * invoices, each row with the cost centre of the origin, as JSON (API) or CSV.
 */
export const erpCostLinesQuerySchema = z
    .object({
    from: isoDate,
    to: isoDate,
    siteId: uuid.optional(),
    companyId: uuid.optional(),
    prestataireId: uuid.optional(),
    kind: z.enum(EXPEDITION_COST_KINDS).optional(),
    /** Only the lines on an approved invoice. */
    invoiced: z.enum(['true', 'false']).optional(),
    format: z.enum(['json', 'csv']).default('json'),
})
    .refine((q) => q.from <= q.to, { path: ['to'], message: 'to must be on or after from' });
export const erpInvoicesQuerySchema = z
    .object({
    from: isoDate,
    to: isoDate,
    companyId: uuid.optional(),
    prestataireId: uuid.optional(),
    format: z.enum(['json', 'csv']).default('json'),
})
    .refine((q) => q.from <= q.to, { path: ['to'], message: 'to must be on or after from' });
/** Spreads an amount over the origins of an expédition by their share of the quantity. */
export function allocateByOrigin(amount, splits) {
    const total = splits.reduce((s, x) => s + Math.max(0, x.quantityT), 0);
    if (!splits.length || total <= 0)
        return [];
    const rows = splits.map((split) => {
        const share = Math.max(0, split.quantityT) / total;
        return { split, share, amount: Math.round(amount * share * 100) / 100 };
    });
    // rounding: the last origin takes the remainder so that the rows add up to the amount
    const diff = Math.round((amount - rows.reduce((s, r) => s + r.amount, 0)) * 100) / 100;
    if (diff !== 0)
        rows[rows.length - 1].amount = Math.round((rows[rows.length - 1].amount + diff) * 100) / 100;
    return rows;
}
//# sourceMappingURL=invoices.js.map